import React from "react"
import flow from "lodash/flow"
import { requestAppList } from "src/engine/modules/applications/actions/creators"
import { requestFeatureList } from "src/engine/modules/features/actions/creators"
import {
  readNotifications,
  markAllRead,
  markOneRead,
  connectWebsocket,
} from "src/engine/modules/notifications/actions/creators"
import SidebarMenu from "@infotech/uikit-menubar"
import {
  CalendarIcon,
  PlanTasksIcon,
  LinkIcon,
  FavOffIcon,
  PlanTasksArchIcon,
  GroupIcon,
} from "@infotech/uikit-icons"

import { Box, Div } from "@infotech/uikit-grid"

import { withRouter } from "react-router-dom"
import { compose } from "recompact"
import { connect } from "react-redux"
import { BASE_ELEMENT_ID } from "src/configuration"
import { logout } from "src/engine/modules/session/actions/creators"
import { readAllStatuses } from "src/engine/modules/status/actions/creators"
import {
  toggleMenu,
  toggleMenuCaptions,
} from "src/engine/modules/ui/actions/creators"
import { selectValue } from "src/engine/modules/ui/selectors"
import { selectApplicationList } from "src/engine/modules/applications/selectors"
import { selectAllNotifications } from "src/engine/modules/notifications/selectors"
import {
  selectNotificationsAvailable,
  selectStatusAvailable,
} from "src/engine/modules/features/selectors"
import { selectRoles, selectUser } from "src/engine/modules/currentUser"
import { SIDEBAR_WIDTH, SIDEBAR_COMPACT_WIDTH } from "src/engine/modules/ui"
import { SUPER_ADMIN } from "src/constants/knownRoles"

import ResourceTypesMenuIcon from "src/icons/resourceTypesMenu.svg"
import EventTypesMenuIcon from "src/icons/eventTypesMenu.svg"
import ReportsMenuIcon from "src/icons/reports.svg"

import {
  CALENDAR_URL,
  PLANS_URL,
  PLAN_TYPES_URL,
  EVENTS_URL,
  EVENT_TYPES_URL,
  RESOURCES_URL,
  RESOURCE_TYPES_URL,
  REPORT_TEMPLATES_URL,
  ROLES_URL,
} from "src/constants/routeNames"
import CustomTopBar from "./TopBar"

import styles from "./styles.css"

const enhancer = compose(
  connect(
    state => ({
      isMenuVisible: selectValue(state, `isMenuVisible`),
      areMenuCaptionsVisible: selectValue(state, `areMenuCaptionsVisible`),
      isAbleToEditRoles: selectRoles(state)[SUPER_ADMIN] || false,
      currentUser: selectUser(state),
      applicationsList: selectApplicationList(state),
      selectAllNotifications: selectAllNotifications(state),
      selectNotificationsAvailable: selectNotificationsAvailable(state),
      selectStatusAvailable: selectStatusAvailable(state),
    }),
    dispatch => ({
      logout: flow(logout, dispatch),
      toggleMenu: flow(toggleMenu, dispatch),
      toggleMenuCaptions: flow(toggleMenuCaptions, dispatch),
      requestAppList: flow(requestAppList, dispatch),
      requestFeatureList: flow(requestFeatureList, dispatch),
      readNotifications: flow(readNotifications, dispatch),
      readAllStatuses: flow(readAllStatuses, dispatch),
      markOneRead: flow(markOneRead, dispatch),
      markAllRead: flow(markAllRead, dispatch),
      connectWebsocket: flow(connectWebsocket, dispatch),
    }),
  ),
)

export default withRouter(
  enhancer(
    ({
      children,
      toggleMenuCaptions,
      areMenuCaptionsVisible,
      logout,
      isAbleToEditRoles,
      currentUser,
      match: { params },
      history: {
        location: { pathname },
      },
      applicationsList,
      selectAllNotifications,
      selectStatusAvailable,
      selectNotificationsAvailable,
      requestAppList,
      requestFeatureList,
      readNotifications,
      readAllStatuses,
      markOneRead,
      markAllRead,
      connectWebsocket,
    }) => (
      <div id={BASE_ELEMENT_ID} className={styles.Base}>
        <CustomTopBar
          currentUser={currentUser}
          logout={logout}
          params={params}
          pathname={pathname}
          applications={applicationsList || []}
          notificationsData={selectAllNotifications}
          requestAppList={requestAppList}
          selectNotificationsAvailable={selectNotificationsAvailable}
          selectStatusAvailable={selectStatusAvailable}
          readNotifications={readNotifications}
          readAllStatuses={readAllStatuses}
          requestFeatureList={requestFeatureList}
          markOneRead={markOneRead}
          markAllRead={markAllRead}
          connectWebsocket={connectWebsocket}
        />
        <Box flex dev-name="Menues" className={styles[`menues-wrapper`]}>
          <Div
            flex
            style={{
              width: areMenuCaptionsVisible
                ? `${SIDEBAR_WIDTH}px`
                : `${SIDEBAR_COMPACT_WIDTH}px`,
            }}
          >
            <SidebarMenu
              className={styles.SidebarMenu}
              sticky
              width={SIDEBAR_WIDTH}
              items={[
                {
                  title: `Календарь`,
                  to: CALENDAR_URL,
                  icon: () => (
                    <div data-testid="sidebar-calendar">
                      <CalendarIcon />
                    </div>
                  ),
                },
                {
                  title: `Планы`,
                  to: PLANS_URL,
                  icon: () => (
                    <div data-testid="sidebar-plans">
                      <PlanTasksIcon />
                    </div>
                  ),
                },
                {
                  title: `Типы планов`,
                  to: PLAN_TYPES_URL,
                  icon: () => (
                    <div data-testid="sidebar-plan-types">
                      <PlanTasksArchIcon />
                    </div>
                  ),
                },
                {
                  title: `Ресурсы`,
                  to: RESOURCES_URL,
                  icon: () => (
                    <div data-testid="sidebar-resources">
                      <LinkIcon />
                    </div>
                  ),
                },
                {
                  title: `События`,
                  to: EVENTS_URL,
                  icon: () => (
                    <div data-testid="sidebar-events">
                      <FavOffIcon />
                    </div>
                  ),
                },
                {
                  title: `Типы ресурсов`,
                  to: RESOURCE_TYPES_URL,
                  icon: () => (
                    <div data-testid="sidebar-resource-types">
                      <ResourceTypesMenuIcon />
                    </div>
                  ),
                },
                {
                  title: `Типы событий`,
                  to: EVENT_TYPES_URL,
                  icon: () => (
                    <div data-testid="sidebar-event-types">
                      <EventTypesMenuIcon />
                    </div>
                  ),
                },
                {
                  title: `Шаблоны отчетов`,
                  to: REPORT_TEMPLATES_URL,
                  icon: () => (
                    <div data-testid="sidebar-report-templates">
                      <ReportsMenuIcon />
                    </div>
                  ),
                },
                ...(isAbleToEditRoles
                  ? [
                      {
                        title: `Роли`,
                        to: ROLES_URL,
                        icon: () => (
                          <div data-testid="sidebar-roles">
                            <GroupIcon />
                          </div>
                        ),
                      },
                    ]
                  : []),
              ]}
              defaultCompact={!areMenuCaptionsVisible}
              onToggleCompact={toggleMenuCaptions}
            />
          </Div>
          <Box auto className={styles[`main-content-wrapper`]}>
            {children}
          </Box>
        </Box>
      </div>
    ),
  ),
)
